<template>
  <div class="space-y-6">
    <!-- Invite Card -->
    <div class="bg-gray-800 rounded-lg p-6">
      <div class="flex items-center gap-3 mb-4">
        <span class="material-icons text-2xl text-cyan-400" aria-hidden="true">person_add</span>
        <h2 class="text-lg font-semibold text-white">{{ $t('workspace.inviteTitle') }}</h2>
      </div>

      <div class="flex flex-col sm:flex-row gap-3">
        <InputText
          v-model="inviteEmail"
          type="email"
          :placeholder="$t('workspace.inviteEmailPlaceholder')"
          :aria-label="$t('workspace.inviteEmailPlaceholder')"
          class="flex-1"
          @keyup.enter="sendInvite"
        />
        <Button
          severity="primary"
          :label="sending ? $t('common.sending') : $t('workspace.inviteButton')"
          :loading="sending"
          :disabled="!inviteEmail.trim() || sending"
          @click="sendInvite"
        >
          <template #icon>
            <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">send</span>
          </template>
        </Button>
      </div>

      <div v-if="inviteError" class="mt-3 bg-red-900/30 border border-red-700/60 rounded-lg p-3 flex items-center gap-2">
        <span class="material-icons text-red-400 text-base">error_outline</span>
        <p class="text-red-300 text-sm">{{ inviteError }}</p>
      </div>
      <div v-if="inviteSuccess" class="mt-3 bg-green-900/30 border border-green-700/60 rounded-lg p-3 flex items-center gap-2">
        <span class="material-icons text-green-400 text-base">check_circle</span>
        <p class="text-green-300 text-sm">{{ $t('workspace.inviteSent') }}</p>
      </div>
    </div>

    <!-- Current Members Card -->
    <div class="bg-gray-800 rounded-lg p-6">
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-3">
          <span class="material-icons text-2xl text-green-400" aria-hidden="true">groups</span>
          <h2 class="text-lg font-semibold text-white">{{ $t('workspace.currentMembers') }}</h2>
        </div>
        <span class="text-xs font-mono text-gray-400 bg-gray-900/50 border border-gray-700 px-2 py-1 rounded-full">
          {{ members.length }}
        </span>
      </div>

      <div v-if="loadingMembers && members.length === 0" class="text-center text-gray-400 py-6">
        <div class="flex justify-center mb-2">
          <ProgressSpinner
            style="width: 2rem; height: 2rem"
            stroke-width="3"
            :aria-label="$t('common.loading')"
          />
        </div>
        <p class="text-sm">{{ $t('common.loading') }}</p>
      </div>
      <ul v-else class="space-y-2">
        <li
          v-for="member in members"
          :key="member._id"
          class="bg-gray-900/50 border border-gray-700 rounded-lg p-3 flex items-center justify-between gap-3"
        >
          <div class="flex items-center gap-3 min-w-0">
            <div class="w-9 h-9 rounded-full bg-cyan-900/40 border border-cyan-700/40 flex items-center justify-center flex-shrink-0">
              <span class="text-cyan-300 text-sm font-semibold">{{ initialsFor(member) }}</span>
            </div>
            <div class="min-w-0">
              <p class="text-white font-medium truncate">{{ member.name }}</p>
              <p class="text-gray-400 text-sm truncate">{{ member.email }}</p>
            </div>
          </div>
          <div class="flex items-center gap-2 flex-shrink-0">
            <span
              v-if="member.isOwner"
              class="text-xs text-cyan-300 bg-cyan-900/40 border border-cyan-700/40 px-2 py-1 rounded-full font-medium"
            >
              {{ $t('workspace.owner') }}
            </span>
            <Button
              v-else
              variant="text"
              severity="danger"
              v-tooltip.top="$t('workspace.removeMember')"
              :aria-label="$t('workspace.removeMember')"
              @click="removeMember(member._id)"
            >
              <template #icon>
                <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">person_remove</span>
              </template>
            </Button>
          </div>
        </li>
      </ul>
    </div>

    <!-- Pending Invites Card -->
    <div v-if="pendingInvites.length > 0" class="bg-gray-800 rounded-lg p-6">
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-3">
          <span class="material-icons text-2xl text-amber-400" aria-hidden="true">schedule</span>
          <h2 class="text-lg font-semibold text-white">{{ $t('workspace.pendingInvitesTitle') }}</h2>
        </div>
        <span class="text-xs font-mono text-gray-400 bg-gray-900/50 border border-gray-700 px-2 py-1 rounded-full">
          {{ pendingInvites.length }}
        </span>
      </div>

      <ul class="space-y-2">
        <li
          v-for="invite in pendingInvites"
          :key="invite._id"
          class="bg-gray-900/50 border border-gray-700 rounded-lg p-3 flex items-center justify-between gap-3"
        >
          <div class="flex items-center gap-3 min-w-0">
            <div class="w-9 h-9 rounded-full bg-amber-900/30 border border-amber-700/40 flex items-center justify-center flex-shrink-0">
              <span class="material-icons text-amber-300 text-base">mail_outline</span>
            </div>
            <div class="min-w-0">
              <p class="text-white truncate">{{ invite.email }}</p>
              <p class="text-gray-400 text-sm truncate">
                {{ $t('workspace.invitedBy') }} {{ invite.invitedBy?.name || '—' }}
              </p>
            </div>
          </div>
          <Button
            variant="text"
            severity="danger"
            class="flex-shrink-0"
            v-tooltip.top="$t('common.cancel')"
            :aria-label="$t('common.cancel')"
            @click="cancelInvite(invite._id)"
          >
            <template #icon>
              <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">cancel</span>
            </template>
          </Button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import api from '../../services/api';

const { t } = useI18n();

const props = defineProps({
  workspaceId: { type: String, required: true },
});

const members = ref([]);
const pendingInvites = ref([]);
const loadingMembers = ref(false);
const inviteEmail = ref('');
const sending = ref(false);
const inviteError = ref('');
const inviteSuccess = ref(false);

function initialsFor(member) {
  const source = (member?.name || member?.email || '?').trim();
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

async function loadData() {
  loadingMembers.value = true;
  try {
    const [membersRes, invitesRes] = await Promise.all([
      api.get(`/workspaces/${props.workspaceId}/members`),
      api.get(`/workspaces/${props.workspaceId}/invites`),
    ]);
    if (membersRes.data.success) members.value = membersRes.data.data;
    if (invitesRes.data.success) pendingInvites.value = invitesRes.data.data;
  } finally {
    loadingMembers.value = false;
  }
}

async function sendInvite() {
  if (!inviteEmail.value.trim() || sending.value) return;
  sending.value = true;
  inviteError.value = '';
  inviteSuccess.value = false;
  try {
    await api.post(`/workspaces/${props.workspaceId}/invites`, { email: inviteEmail.value.trim() });
    inviteEmail.value = '';
    inviteSuccess.value = true;
    await loadData();
  } catch (err) {
    inviteError.value = err.response?.data?.error?.message || t('workspace.inviteError');
  } finally {
    sending.value = false;
  }
}

async function removeMember(userId) {
  await api.delete(`/workspaces/${props.workspaceId}/members/${userId}`);
  await loadData();
}

async function cancelInvite(inviteId) {
  await api.delete(`/workspaces/${props.workspaceId}/invites/${inviteId}`);
  await loadData();
}

watch(() => props.workspaceId, loadData);
onMounted(loadData);
</script>
